import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import { Card } from './AnalyticsPrimitives';

// Modern, dependency-free date-range filter for the analytics suite. Renders a
// row of quick presets plus a custom month-grid calendar popover, all styled to
// match the app's pink design system (light + dark mode).

const Icon: React.FC<{ component: IconType; className?: string }> = ({ component: Cmp, className }) => {
  const C = Cmp as unknown as React.ComponentType<{ className?: string }>;
  return <C className={className} />;
};

/** Inclusive date range. ``start: null`` means "all time" (no lower bound). */
export interface DateRange {
  start: string | null;
  end: string;
}

export type PresetKey = 'month' | 'quarter' | 'year' | 'all';

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: 'month', label: '1 month' },
  { key: 'quarter', label: '1 quarter' },
  { key: 'year', label: '1 year' },
  { key: 'all', label: 'All' },
];

// --------------------------------------------------------------------------- //
// Date helpers (operate in local time so they line up with the calendar grid).
// --------------------------------------------------------------------------- //

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseISO(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, d.getDate());
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

export function presetRange(key: PresetKey): DateRange {
  const today = startOfDay(new Date());
  const end = toISO(today);
  switch (key) {
    case 'month':
      return { start: toISO(addMonths(today, -1)), end };
    case 'quarter':
      return { start: toISO(addMonths(today, -3)), end };
    case 'year':
      return { start: toISO(addMonths(today, -12)), end };
    case 'all':
    default:
      return { start: null, end };
  }
}

/** Default range shown on first page load (last 1 year). */
export function defaultDateRange(): DateRange {
  return presetRange('year');
}

function activePreset(value: DateRange): PresetKey | 'custom' {
  for (const p of PRESETS) {
    const r = presetRange(p.key);
    if (r.start === value.start && r.end === value.end) return p.key;
  }
  return 'custom';
}

const LABEL_FMT = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

function rangeLabel(value: DateRange): string {
  const ap = activePreset(value);
  const preset = PRESETS.find((p) => p.key === ap);
  if (preset) return preset.key === 'all' ? 'All time' : `Last ${preset.label}`;
  const endLabel = LABEL_FMT.format(parseISO(value.end));
  if (!value.start) return `Up to ${endLabel}`;
  return `${LABEL_FMT.format(parseISO(value.start))} – ${endLabel}`;
}

const PILL_BASE = 'px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-colors';

function pillClass(active: boolean): string {
  return active
    ? `${PILL_BASE} bg-[#FF3EB5] text-white shadow-sm`
    : `${PILL_BASE} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600`;
}

// --------------------------------------------------------------------------- //
// Calendar popover
// --------------------------------------------------------------------------- //

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_FMT = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });

function buildMonthGrid(view: Date): Date[] {
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const startDow = first.getDay();
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(first.getFullYear(), first.getMonth(), 1 - startDow + i));
  }
  return cells;
}

const CalendarPopover: React.FC<{
  value: DateRange;
  onApply: (v: DateRange) => void;
  inline?: boolean;
}> = ({ value, onApply, inline = false }) => {
  const today = startOfDay(new Date());
  const initialEnd = parseISO(value.end);
  const [view, setView] = useState<Date>(new Date(initialEnd.getFullYear(), initialEnd.getMonth(), 1));
  const [draftStart, setDraftStart] = useState<Date | null>(value.start ? parseISO(value.start) : null);
  const [draftEnd, setDraftEnd] = useState<Date | null>(value.start ? parseISO(value.end) : null);

  const grid = useMemo(() => buildMonthGrid(view), [view]);

  const handlePick = (d: Date) => {
    if (d > today) return; // no future dates — there is no data ahead of today
    if (!draftStart || draftEnd) {
      setDraftStart(d);
      setDraftEnd(null);
    } else if (d < draftStart) {
      setDraftEnd(draftStart);
      setDraftStart(d);
    } else {
      setDraftEnd(d);
    }
  };

  const canApply = !!(draftStart && draftEnd);
  const apply = () => {
    if (draftStart && draftEnd) onApply({ start: toISO(draftStart), end: toISO(draftEnd) });
  };

  const inRange = (d: Date): boolean =>
    !!(draftStart && draftEnd && d >= draftStart && d <= draftEnd);

  return (
    // z-index must clear Leaflet's map panes/controls (up to ~1000) so the calendar
    // popover renders above the heatmap on the Geo tab.
    <div
      className={inline
        ? 'w-full'
        : 'absolute right-0 top-full z-[1200] mt-2 w-72 rounded-xl bg-white dark:bg-gray-800 p-3 shadow-xl ring-1 ring-gray-200 dark:ring-gray-700'}
      role="dialog"
      aria-label="Choose a date range"
    >
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setView(addMonths(view, -1))}
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          aria-label="Previous month"
        >
          <Icon component={FaChevronLeft} className="text-xs" />
        </button>
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {MONTH_FMT.format(view)}
        </span>
        <button
          type="button"
          onClick={() => setView(addMonths(view, 1))}
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          aria-label="Next month"
        >
          <Icon component={FaChevronRight} className="text-xs" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-[11px] font-medium text-gray-400 dark:text-gray-500 py-1">
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {grid.map((d) => {
          const isCurrentMonth = d.getMonth() === view.getMonth();
          const isFuture = d > today;
          const isStart = draftStart && sameDay(d, draftStart);
          const isEnd = draftEnd && sameDay(d, draftEnd);
          const isEdge = isStart || isEnd;
          const within = inRange(d) && !isEdge;
          const isToday = sameDay(d, today);
          return (
            <button
              key={toISO(d)}
              type="button"
              disabled={isFuture}
              onClick={() => handlePick(d)}
              className={[
                'h-8 text-xs rounded-lg transition-colors',
                isFuture ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : 'cursor-pointer',
                !isCurrentMonth && !isFuture ? 'text-gray-400 dark:text-gray-500' : '',
                isCurrentMonth && !isFuture && !isEdge ? 'text-gray-700 dark:text-gray-200' : '',
                isEdge ? 'bg-[#FF3EB5] text-white font-semibold' : '',
                within ? 'bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-200' : '',
                !isEdge && !within && !isFuture ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : '',
                isToday && !isEdge ? 'ring-1 ring-inset ring-pink-300 dark:ring-pink-500' : '',
              ].filter(Boolean).join(' ')}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-[11px] text-gray-500 dark:text-gray-400">
          {draftStart ? LABEL_FMT.format(draftStart) : 'Start'}
          {' – '}
          {draftEnd ? LABEL_FMT.format(draftEnd) : 'End'}
        </span>
        <button
          type="button"
          onClick={apply}
          disabled={!canApply}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            canApply
              ? 'bg-[#FF3EB5] hover:bg-[#E635A3] text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
          }`}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------- //
// Compact variant (table column filter)
// --------------------------------------------------------------------------- //

// Compact trigger + popover reusing the same presets and calendar as the full
// bar. The popover is ``position: fixed`` (positioned from the trigger's rect)
// so it is never clipped when hosted inside an ``overflow:auto`` table.
export const DateRangeFilterCompact: React.FC<{
  value: DateRange;
  onChange: (v: DateRange) => void;
  className?: string;
}> = ({ value, onChange, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const active = activePreset(value);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 288; // w-72
    const estH = 430;
    let left = Math.max(8, r.left);
    if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - 8 - width);
    let top = r.bottom + 4;
    if (top + estH > window.innerHeight - 8) top = Math.max(8, r.top - estH - 4); // flip up
    setPos({ top, left });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onScroll = () => place();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true); // capture also catches container scroll
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Filter by date"
        className={`w-full flex items-center justify-between gap-1 rounded border px-2 py-1 text-xs ${
          active !== 'all'
            ? 'border-pink-400 text-pink-600 dark:text-pink-300 bg-pink-50 dark:bg-pink-500/10'
            : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 bg-white dark:bg-gray-800'
        }`}
      >
        <span className="truncate">{active === 'all' ? 'All' : rangeLabel(value)}</span>
        <Icon component={FaCalendarAlt} className="h-3 w-3 shrink-0" />
      </button>
      {open && (
        <div
          ref={popRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
          className="z-[1200] w-72 rounded-xl bg-white dark:bg-gray-800 p-2 shadow-xl ring-1 ring-gray-200 dark:ring-gray-700"
        >
          <div className="mb-2 flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => { onChange(presetRange(p.key)); setOpen(false); }}
                className={pillClass(active === p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <CalendarPopover value={value} inline onApply={(v) => { onChange(v); setOpen(false); }} />
        </div>
      )}
    </div>
  );
};

// --------------------------------------------------------------------------- //
// Main filter bar
// --------------------------------------------------------------------------- //

const DateRangeFilter: React.FC<{
  value: DateRange;
  onChange: (v: DateRange) => void;
  className?: string;
}> = ({ value, onChange, className = '' }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const active = activePreset(value);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const applyPreset = (key: PresetKey) => {
    onChange(presetRange(key));
    setOpen(false);
  };

  return (
    <Card className={`p-3 sm:p-4 ${className}`}>
      <div
        ref={containerRef}
        className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-pink-500 shrink-0">
            <Icon component={FaCalendarAlt} />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
              {rangeLabel(value)}
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500">Filter results by date</div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p.key)}
              className={pillClass(active === p.key)}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-haspopup="dialog"
            className={`${pillClass(active === 'custom')} inline-flex items-center gap-1`}
          >
            Custom
            <Icon component={FaChevronDown} className="text-[10px]" />
          </button>
        </div>

        {open && (
          <CalendarPopover
            value={value}
            onApply={(v) => {
              onChange(v);
              setOpen(false);
            }}
          />
        )}
      </div>
    </Card>
  );
};

export default DateRangeFilter;
